import useUrlState from '@ahooksjs/use-url-state';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { ProForm, ProFormField, ProFormFieldProps, ProFormInstance, ProFormProps } from '@ant-design/pro-form';
import { CharacterInputValue } from '@tencent/tds-ui';
import { useHover, useLocalStorageState } from 'ahooks';
import { Button, Divider, InputNumberProps, InputProps, message } from 'antd';
import classNames from 'classnames';
import { isString, get } from 'lodash';
import moment, { Moment } from 'moment';
import React, { ReactNode, useMemo, useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import SparkMD5 from 'spark-md5';
import  StringTooltipIcon from '@/assets/images/icons/string-tooltip-icon.react.svg?react';
import CompareFormA from '@/assets/images/public/compare-form-a.svg';
import CompareFormB from '@/assets/images/public/compare-form-b.svg';
import { BubbleWithProUpgrade } from '@/config/app/auth';
import { logger } from '@/infrastructure/logger';
import { getProductId } from '@/infrastructure/product-info';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { judgmentTime } from '@/infrastructure/utils/issue';
import {
  reportFiltersEditSearchCriteria,
} from '@/presentation/components/exception-form/utils';
import { TdsArea, TdsAreaFieldsPanelProps } from '@/presentation/components/tds-form/tds-area';
import { TdsCharacterInput, TdsCharacterInputProps } from '@/presentation/components/tds-form/tds-character-input';
import { TdsCustomRange, TdsCustomRangeProps } from '@/presentation/components/tds-form/tds-custom-range';
import {
  TdsDateCascader,
  TdsDateCascaderProps,
  TdsDateCascaderValue,
} from '@/presentation/components/tds-form/tds-date-cascader';
import { TdsFieldSelector, TdsFieldSelectorProps } from '@/presentation/components/tds-form/tds-field-selector';
import { TdsFieldSelectorEnableExclude, TdsFieldSelectorEnableExcludeProps  } from '@/presentation/components/tds-form/tds-field-selector-enable-exclude';
import { TdsFilterPanel } from '@/presentation/components/tds-form/tds-fields-filter';
import { TdsFormLabel } from '@/presentation/components/tds-form/tds-form-label';
import { TdsNumberInput, TdsNumberInputProps } from '@/presentation/components/tds-form/tds-number-input';
import { TdsSearchHistory } from '@/presentation/components/tds-form/tds-search-history/save-search';
import { TDS_SEARCH_VALUES, TdsColumnGroup, TdsSearchValueStorageType } from '@/presentation/components/tds-form/type';
import {
  dateCascader2Filter,
  field2Filter,
  isValidFilter,
  number2Filter,
  simpleField2Filter,
  version2Filter,
  customFields2Filter, enumFields2Filter,
  dynamicGenTimeRange, filterFormValues,
  standardVersion2Filter,
  enableExcludeFields2Filter,
} from '@/presentation/components/tds-form/utils';
import { useTdsForm } from '@/presentation/hooks/form';
import { useStores } from '@/presentation/store';
import { StandardCommonFieldKey } from '@/proto/field';
import { IFilter } from '@/proto/metric';
import { parseStandardCompareToken } from '@/proto/token';
import { CUSTOM, shortRanges } from '../form/components/time-range-cascade';
import { MatchMode } from '../form/standards/types';
import { hintChildren } from '../hint-text/hintText';
import { SplitModal } from '../split-modal';
import styles from './index.module.scss';
import { TdsCustomFields, TdsCustomFieldsProps } from './tds-custom-fields';
import { TdsCustomFieldsPanel, TdsCustomFieldsPanelProps } from './tds-custom-panel';
import { TdsInput, TdsInputNumber, TdsInputNumberProps, TdsInputProps } from './tds-input';


export enum TdsFormItemType {
  CharacterInput = 'characterInput',
  FieldSelector = 'fieldSelector',
  FieldInput = 'fieldInput',
  FieldInputNumber = 'fieldInputNumber',
  DateCascader = 'dateCascader',
  NumberInput = 'numberInput',
  // 自定义字段（新版，用于hippy里面的耗时区间）
  CustomFields = 'customFields',
  // 自定义操作符范围
  CustomRange = 'customRange',
  // 支持排除的select
  FieldSelectorEnableExclude='fieldSelectorEnableExclude',
  // 自定义字段和自定义维度（旧版）
  CustomPanel = 'customPanel',
  Area = 'area' // 地域
}

export enum TdsFilterType {
  String = 'string',
  StringArray = 'stringArray',
  Version = 'version',
  StandardVersion = 'standardVersion',
  Number = 'number',
  Custom = 'custom',
  DateCascader = 'dateCascader',
  SingleString = 'singleString',
  EnumString = 'enumString', // 可多选枚举类型
  EnableExclude = 'enableExclude', // 支持排除（反选）组件的filter转换
}

// @ts-ignore
export interface TdsFormItem extends Omit<ProFormFieldProps, 'width'> {
  width?: number;
  hidden?: boolean;
  type: TdsFormItemType;
  labelString?: string;
  labelTools?: React.ReactNode[];
  fieldProps?: TdsCharacterInputProps
  | TdsFieldSelectorProps
  | TdsDateCascaderProps
  | TdsCustomRangeProps
  | TdsCustomFieldsPanelProps
  | TdsInputNumberProps
  | TdsInputProps
  | TdsNumberInputProps
  | TdsCustomFieldsProps
  | TdsFieldSelectorEnableExcludeProps;
  deleteable?: boolean;
  // 字段在提交时 转换到filter结构体里面
  inFilter?: boolean;
  filterName?: string;
  // 自定义转换到filter里的逻辑
  filterTransform?: (fieldKey: string, fieldValue: any, allValues: Record<string, any>) => IFilter[];
  // 自定义从filter里面转换到表单值的逻辑
  filterConvert?: () => void;
  // filter类型，对应转换逻辑。custom配合filterTransform使用
  filterType?: TdsFilterType;
  // 筛选项是否隐藏，可以在添加字段处手动编辑展示
  defaultEditHidden?: boolean;
  // 对比模式下是否支持该字段
  hideInCompare?: boolean;
  batchEditMode?: boolean;
  batchEditTips?: ReactNode;
  group?: TdsColumnGroup;
  /**
   * 字段联动配置（内部使用，不暴露给外部）
   * @internal
   */
  reactions?: import('./tds-schema-form/schema').FieldReactions;
}

export type CompareParams = {
  fieldsValue: Record<string, any>,
  fieldsFilters: IFilter[],
  compareFieldsValue: Record<string, any>,
  compareFieldsFilters: IFilter[],
  uuid?: string;
  isCompare?: boolean;
};

type IFormInstance = React.MutableRefObject<ProFormInstance<any>
| undefined> | React.RefObject<ProFormInstance<any> | undefined>;

export interface TdsFormProps extends Omit<ProFormProps, 'children' | 'onFinish'> {
  // 表单唯一标识
  name: string;
  items?: TdsFormItem[];
  onFinish?: (values: Record<string, any>, filters: IFilter[]) => void;
  onCompareFinish?: (values: CompareParams) => void;
  // 展示历史记录
  showSearchHistory?: boolean;
  // 展示字段管理按钮
  showFilterPanel?: boolean;
  // 显示展开收起按钮
  showToggle?: boolean;
  // 显示确定按钮
  showSubmit?: boolean;
  pageId: string;
  tokenControl?: TokenControl;
  showToolbar?: boolean;
  // 对比模式打开关闭控制
  compareOpen?: boolean;
  // 是否支持对比模式，控制切换按钮显示
  enableCompare?: boolean;
  compareFormRef?: IFormInstance;
  // 改变对比模式触发表单提交
  changeCompareModeWithSubmit?: boolean;
  // 初始化token处理逻辑完成
  onTokenInited?: () => void;
  onFormChange?: (params: CompareParams) => void;
  autoSubmit?: boolean;
  renderByGroup?: boolean;
}

export interface ITdsFormRef {
  getFilter: () => IFilter[];
  // 适用于对比模式下提交
  submitAll: () => void;
  showFormItem: (formItem: TdsFormItem[], formValues: Record<string, any>) => Promise<void>;
  getFormInstance: () => any;
  getCompareFormInstance?: () => any;
  triggerFormChange?: () => void;
}

const DEFAULT_ITEM_WIDTH = 266;

export interface TokenControl {
  refreshToken?: boolean;
  replaceToken?: boolean;
  onTokenChange?: (token: string, fieldsValue: Record<string, any>, filters: IFilter[]) => void;
  autoSubmit?: boolean;
  onParsed?: (values: any) => void;
  isExpiredDateModal?: {
    isAnalytics: boolean;
    initDate: TdsDateCascaderValue;
  }; // 带 token 的链接时间过期是否弹窗提醒
}

export const TdsForm = forwardRef<ITdsFormRef, TdsFormProps>((
  props: TdsFormProps,
  ref: React.ForwardedRef<ITdsFormRef>,
) => {
  const {
    name,
    items = [],
    className,
    onFinish,
    showFilterPanel = true,
    showSearchHistory = true,
    showToggle = true,
    showSubmit = true,
    showToolbar = true,
    compareOpen = false,
    enableCompare = false,
    compareFormRef: targetFormRef,
    formRef: sourceFormRef,
    changeCompareModeWithSubmit = true,
    ...formProps
  } = props;

  const splitRef = useRef<any>();
  const [, useTdsFormContext] = useTdsForm;
  const { updateFormContext } = useTdsFormContext();
  const [isCompare, setIsCompare] = useState(compareOpen);
  const [collapsed, setCollapsed] = useState(false);
  const { isFreeProduct } = useStores().ProductStore;

  const [state, setState] = useUrlState<Record<string, any>>({
    token: '',
  });

  // const enableCompareStrick = props.enableCompare && authorityNodes?.includes(AuthNodes.CompareMode);
  const enableCompareStrick = props.enableCompare && isFreeProduct();

  // 历史记录
  const [searchValues, setSearchValues] = useLocalStorageState<TdsSearchValueStorageType>(`${TDS_SEARCH_VALUES}-${name}-${getProductId()}`, {
    defaultValue: [],
  });

  const enableItems = items.filter(entry => entry.hidden !== true)
    .filter(entry => !compareOpen || (compareOpen && !entry.hideInCompare));
  // 不可编辑字段
  const defaultItems = enableItems.filter(entry => entry.deleteable === false);
  // 可编辑字段
  const editableItems = enableItems.filter(entry => entry.deleteable !== false);
  const editableItemNames = editableItems?.filter(item => !item.defaultEditHidden).map(item => item.name);
  // 字段控制+缓存
  const storageKey = `storage-tds-form-items-${name}-${SparkMD5.hash(items.toString())}-v2`;
  const [filteredItemNames, setFilteredItemNames] = useLocalStorageState<string[]>(storageKey, {
    defaultValue: editableItemNames as string[],
  });
  // 用于存储在【添加字段里】取消勾选了，但是有value值的表单项
  const [filterValuesNames, setFilterValuesNames] = useState<string[]>([]);

  const editFormInstance = useRef<any>();
  const editItem = useRef<TdsFormItem>();


  useImperativeHandle(ref, () => ({
    getFilter,
    showFormItem,
    submitAll: onCompareSubmit,
    triggerFormChange,
    getFormInstance: () => sourceFormRef?.current,
    getCompareFormInstance: () => targetFormRef?.current,
  }));

  const showFormItem = async (formItem: TdsFormItem[], formValues: Record<string, any>) => {
    setFilterValuesNames(item => [...new Set((item || []).concat(filterFormValues(formItem
      ?.filter(name => name.deleteable !== false
        && !filteredItemNames?.includes(String(name?.name))), formValues)))]);
  };


  const filteredItems = useMemo(
    () => items.filter(item => filteredItemNames?.includes(item.name as string)),
    [filteredItemNames],
  );

  // 筛选
  const filterValueItems = useMemo(
    () => editableItems.filter(item => filterValuesNames?.includes(String(item.name))
      && !filteredItemNames?.includes(String(item.name))),
    [filterValuesNames, filteredItemNames, editableItems],
  );

  const onBatchEditConfirm = (value?: string[]) => {
    const formInstance = editFormInstance;
    const item = editItem.current as TdsFormItem;
    const fieldValue: CharacterInputValue = formInstance?.current?.getFieldValue(item.name ?? '');

    if ([MatchMode.Between, MatchMode.NOTEMPTY, MatchMode.ISEMPTY].includes(fieldValue?.mode as MatchMode)) {
      message.warning('请在多选的匹配模式下使用');
      return;
    }

    formInstance?.current?.setFieldsValue({
      [item.name as string]: {
        ...fieldValue,
        values: value || [],
      },
    });
  };

  const showModal = (item: TdsFormItem, formType: string) => {
    const formInstance = formType === 'source' ? sourceFormRef : targetFormRef;
    const fieldValue: CharacterInputValue = formInstance?.current?.getFieldValue(item.name ?? '');

    if ([
      MatchMode.Between,
      MatchMode.NOTEMPTY,
      MatchMode.ISEMPTY,
      MatchMode.Ge,
      MatchMode.Gt,
      MatchMode.Le,
      MatchMode.Lt,
    ].includes(fieldValue?.mode as MatchMode)) {
      message.warning('请在支持多选的匹配模式下使用');
      return;
    }

    const values = fieldValue?.values;
    const formatValues = isString(values) ? [values] : values?.join('\n');

    splitRef.current?.showModal(formatValues);
    editFormInstance.current = formInstance?.current;
    editItem.current = item;
  };

  const renderFormItem = (item: TdsFormItem, formType: string) => {
    const {
      width,
      fieldProps,
    } = item;
    const itemWidth = width || DEFAULT_ITEM_WIDTH;
    let child: ReactNode;
    switch (item.type) {
      case TdsFormItemType.CharacterInput:
        child = <TdsCharacterInput {...fieldProps as TdsCharacterInputProps} />;
        break;
      case TdsFormItemType.FieldSelector:
        child = <TdsFieldSelector placeholder={'请选择'} {...fieldProps as TdsFieldSelectorProps} />;
        break;
      case TdsFormItemType.FieldInput:
        child = <TdsInput placeholder={'请选择'} {...fieldProps as InputProps} />;
        break;
      case TdsFormItemType.FieldInputNumber:
        child = <TdsInputNumber placeholder={'请选择'} {...fieldProps as InputNumberProps} />;
        break;
      case TdsFormItemType.DateCascader:
        child = <TdsDateCascader {...fieldProps as TdsDateCascaderProps} />;
        break;
      case TdsFormItemType.NumberInput:
        child = <TdsNumberInput {...fieldProps as TdsNumberInputProps} />;
        break;
      case TdsFormItemType.CustomRange:
        child = <TdsCustomRange {...fieldProps as TdsCustomRangeProps} />;
        break;
      case TdsFormItemType.CustomFields:
        child = <TdsCustomFields {...fieldProps as TdsCustomFieldsProps} />;
        break;
      case TdsFormItemType.FieldSelectorEnableExclude:
        child = <TdsFieldSelectorEnableExclude placeholder={'请选择'} {...fieldProps as TdsFieldSelectorEnableExcludeProps }/>;
        break;
      case TdsFormItemType.CustomPanel:
        child = <TdsCustomFieldsPanel {...fieldProps as TdsCustomFieldsPanelProps} />;
        break;
      case TdsFormItemType.Area:
        child = <TdsArea {...fieldProps as TdsAreaFieldsPanelProps} />;
        break;
      default:
        break;
    }


    return (
      <FormField
        item={item}
        itemWidth={itemWidth}
        formType={formType}
        showModal={showModal}
        splitRef={splitRef}
        onBatchEditConfirm={onBatchEditConfirm}
      >
        {child}
      </FormField>
    );
  };

  // 实际要渲染的字段
  const renderFormItems = (formType: string) => {
    const enableItemNames = defaultItems.concat(filteredItems).map(item => item.name);
    const enableItems = items.filter(entry => entry.hidden !== true)
      .filter(entry => !compareOpen || (compareOpen && !entry.hideInCompare))
      .filter(item => enableItemNames.includes(item.name));

    // 按选择顺序做排序
    const defaultSortItems = enableItems.filter(item => !filteredItemNames?.includes((item.name ?? '') as string));
    const sortItems: TdsFormItem[] = filteredItemNames?.
      map(item => enableItems.find(subItem => subItem.name === item) as TdsFormItem) ?? [];
    const finalItems = defaultSortItems.concat(sortItems).filter(item => !!item);

    return finalItems.concat(filterValueItems).map(item => renderFormItem(item, formType));
  };

  const transform2Filter = (item: TdsFormItem, value: any, allValues: Record<string, any>): IFilter[] => {
    const filterName = item.filterName || item.name;
    switch (item.filterType) {
      case TdsFilterType.Version:
        return version2Filter(filterName as string, value);
      case TdsFilterType.StandardVersion:
        return standardVersion2Filter(filterName as string, value);
      case TdsFilterType.DateCascader:
        // 对时间范围组件进行特殊处理，非自定义，动态更新时间值
        if (value?.short_cut !== CUSTOM) {
          const customShortRanges: Record<string, () => Moment[]> = get(
            items?.find?.(item => item.name
              === StandardCommonFieldKey.TimeRange)?.fieldProps,
            'customShortRanges',
          ) || shortRanges;
          const ranges = customShortRanges[value?.short_cut]?.();
          // eslint-disable-next-line no-param-reassign
          value.date_value = [moment(ranges[0]).format('YYYY-MM-DD HH:mm:ss'), moment(ranges[1]).format('YYYY-MM-DD HH:mm:ss')];
        }

        return dateCascader2Filter(value);
      case TdsFilterType.Number:
        return number2Filter(filterName as string, value);
      case TdsFilterType.SingleString:
        return simpleField2Filter(filterName as string, value);
      case TdsFilterType.StringArray:
        return customFields2Filter(filterName as string, value);
      case TdsFilterType.EnumString:
        return enumFields2Filter(String(filterName), value);
      case TdsFilterType.EnableExclude:
        return enableExcludeFields2Filter(String(filterName), value);
      case TdsFilterType.Custom:
        return item.filterTransform ? item.filterTransform(filterName as string, value, allValues) : [];
      default:
        return field2Filter(filterName as string, value);
    }
  };

  // 用于同一个页面有多个表单，且多个表单的filter需要合并处理
  const getFilter = (): IFilter[] => {
    const values = sourceFormRef?.current?.getFieldsFormatValue?.() ?? {};
    const filters: IFilter[] = [];
    Object.keys(values).forEach((fieldKey: string) => {
      const currentItem = items.find(item => item.name === fieldKey);
      const fieldValue = values[fieldKey];
      if (currentItem?.inFilter) {
        const currentFilter = transform2Filter(currentItem, fieldValue, values);
        currentFilter && filters.push(...currentFilter);
      }
    });

    // 过滤值为空的filter
    const notEmptyFilters = filters.filter(filter => isValidFilter(filter));

    return notEmptyFilters ?? [];
  };


  const onSubmit = (allFields?: Record<string, any>) => {
    const fields = allFields ? {
      values: allFields,
      filters: allFields?.filters ?? [],
    } : genFormFields(sourceFormRef?.current);


    onFinish?.(fields.values, fields.filters);
    // 数据上报
    try {
      reportFiltersEditSearchCriteria(fields.filters, props.pageId);
      reportClick(props.pageId, CUSTOM_ACTIONS.commonDoSearch, {
        from: props.pageId,
        param3: JSON.stringify(fields.values),
        param4: JSON.stringify(fields.filters),
      });
    } catch (e) {
      logger.error('report error', e);
    }
  };

  const genFormFields = (form?: any) => {
    const values = form?.getFieldsFormatValue?.() ?? {};
    const filters: IFilter[] = [];

    // 更新value里面时间
    if (values?.[StandardCommonFieldKey.TimeRange]) {
      const date = values[StandardCommonFieldKey.TimeRange];
      if (date?.short_cut !== CUSTOM) {
        const customShortRanges: Record<string, () => Moment[]> = get(
          items?.find?.(item => item.name
          === StandardCommonFieldKey.TimeRange)?.fieldProps,
          'customShortRanges',
        ) || shortRanges;
        const ranges = customShortRanges?.[date?.short_cut]?.();
        values[StandardCommonFieldKey.TimeRange].date_value = [moment(ranges[0]).format('YYYY-MM-DD HH:mm:ss'), moment(ranges[1]).format('YYYY-MM-DD HH:mm:ss')];
      }
    }

    Object.keys(values).forEach((fieldKey: string) => {
      const currentItem = items.find(item => item.name === fieldKey);
      const fieldValue = values[fieldKey];
      if (currentItem?.inFilter) {
        const currentFilter = transform2Filter(currentItem, fieldValue, values);
        currentFilter && filters.push(...currentFilter);
      }
    });
    // 过滤值为空的filter
    const notEmptyFilters = filters.filter(filter => isValidFilter(filter));
    return {
      values,
      filters: notEmptyFilters,
    };
  };

  // 对比模式下提交
  const onCompareSubmit = (allFieldsA?: Record<string, any>, allFieldsB?: Record<string, any>) => {
    const fieldsA = allFieldsA ? {
      values: allFieldsA,
      filters: allFieldsA?.filters ?? [],
    } : genFormFields(sourceFormRef?.current);
    const fieldsB = allFieldsB ? {
      values: allFieldsB,
      filters: allFieldsB?.filters ?? [],
    } : genFormFields(targetFormRef?.current);

    props.onCompareFinish?.({
      fieldsValue: fieldsA.values,
      fieldsFilters: fieldsA.filters,
      compareFieldsValue: fieldsB.values,
      compareFieldsFilters: fieldsB.filters,
    });
  };

  const onToggle = () => {
    setCollapsed(!collapsed);
  };

  const onSelectedChange = (entries: TdsFormItem[]) => {
    const entriesNames = entries.map(item => String(item.name));
    setFilteredItemNames(entriesNames);
  };

  const initToken = async () => {
    const { autoSubmit = true, onParsed, isExpiredDateModal } = props.tokenControl ?? {};
    // 没有token
    if (!state.token) {
      !isCompare ? sourceFormRef?.current?.submit() : onCompareSubmit();
      return;
    }

    const res = await parseStandardCompareToken(state.token);
    const { isCompare: tokenCompare, formListA, formListB, ...formList } = res;

    const isCompareOpen = enableCompareStrick ? tokenCompare : false;
    // 如果设置的是自定义，需要动态更新时间,然后重新生成token
    const dynamicRange = formList?.[StandardCommonFieldKey.TimeRange]
      ? dynamicGenTimeRange(formList?.[StandardCommonFieldKey.TimeRange]) : undefined;
    const dynamicValues = {
      ...formList,
      [StandardCommonFieldKey.TimeRange]: dynamicRange !== undefined ? dynamicRange
        : props.initialValues?.[StandardCommonFieldKey.TimeRange],
    };

    // 链接有token时，强制展示有value值的表单项
    showFormItem(items, dynamicValues)?.then?.(() => {
      sourceFormRef?.current?.setFieldsValue?.(dynamicValues);
    });


    let dynamicValuesB = {};
    if (isCompareOpen && formListB) {
      const dynamicRangeB = formListB?.[StandardCommonFieldKey.TimeRange]
        ? dynamicGenTimeRange(formListB?.[StandardCommonFieldKey.TimeRange]) : undefined;
      dynamicValuesB = {
        ...formListB,
        [StandardCommonFieldKey.TimeRange]: dynamicRangeB !== undefined ? dynamicRangeB
          : props.initialValues?.[StandardCommonFieldKey.TimeRange],
      };

      targetFormRef?.current?.setFieldsValue(dynamicValuesB);
    }

    onParsed?.({
      fields: dynamicValues,
      fieldsCompare: dynamicValuesB,
    });

    setIsCompare(isCompareOpen);

    if (isExpiredDateModal) {
      const isExpired = judgmentTime?.(
        moment(dynamicRange?.date_value?.[0]),
        moment(dynamicRange?.date_value?.[0]),
        dynamicRange?.time_window_type,
        isExpiredDateModal?.isAnalytics,
        true, () => {
          sourceFormRef?.current?.setFieldsValue({ [StandardCommonFieldKey.TimeRange]: isExpiredDateModal.initDate });
        },
      );
      let isExpiredCompare = false;
      if (isCompareOpen && formListB && !isExpired) {
        const dynamicRangeB = formListB?.[StandardCommonFieldKey.TimeRange]
          ? dynamicGenTimeRange(formListB?.[StandardCommonFieldKey.TimeRange]) : undefined;
        isExpiredCompare = judgmentTime?.(
          moment(dynamicRangeB?.date_value?.[0]),
          moment(dynamicRangeB?.date_value?.[0]),
          dynamicRangeB?.time_window_type,
          isExpiredDateModal?.isAnalytics,
          true, () => {
            targetFormRef?.current?.setFieldsValue({ [StandardCommonFieldKey.TimeRange]: isExpiredDateModal.initDate });
          },
        );
      }
      if (isExpired || isExpiredCompare) {
        return;
      }
    }

    if (!autoSubmit) return;

    // 注意，重新生成token不一定会是新的
    if (!isCompareOpen) {
      onSubmit(dynamicValues);
    } else {
      onCompareSubmit(dynamicValues, dynamicValuesB);
    }
  };

  const toggleCompare = () => {
    setIsCompare(!isCompare);
    // 切换模式重新提交表单
    if (changeCompareModeWithSubmit) {
      return isCompare ? onSubmit() : onCompareSubmit();
    }
  };

  const onFieldsChange = (changedFields: any, allFields: any) => {
    const fieldsA = genFormFields(sourceFormRef?.current);
    const fieldsB = genFormFields(targetFormRef?.current);

    props.onFormChange?.({
      fieldsValue: fieldsA.values,
      fieldsFilters: fieldsA.filters,
      compareFieldsValue: fieldsB.values,
      compareFieldsFilters: fieldsB.filters,
    });

    props.onFieldsChange?.(changedFields, allFields);
  };

  const triggerFormChange = () => {
    const fieldsA = genFormFields(sourceFormRef?.current);
    const fieldsB = genFormFields(targetFormRef?.current);

    props.onFormChange?.({
      fieldsValue: fieldsA.values,
      fieldsFilters: fieldsA.filters,
      compareFieldsValue: fieldsB.values,
      compareFieldsFilters: fieldsB.filters,
    });
  };

  const compareText = useMemo(() => (isCompare ? '退出对比' : '条件对比'), [isCompare]);

  useEffect(() => {
    if (props.tokenControl?.refreshToken) {
      initToken();
    }

    if (props.autoSubmit) {
      return !isCompare ? onSubmit() : onCompareSubmit();
    }
  }, []);

  useEffect(() => {
    setIsCompare(compareOpen);
  }, [compareOpen]);

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    updateFormContext?.(draft => ({
      ...draft,
      isCompare: !!isCompare,
    }));
    setState({
      isCompare,
    });
  }, [isCompare]);

  return (
    <div className={classNames(styles.tdsForm, className)}>
      <div>
        <div className={styles.tdsFormContainer}>
          {isCompare && <div className={styles.compareLabel}>
            <span className={styles.compareText}>条件</span>
            <img src={CompareFormA} className={styles.compareImg}/>
          </div>}
          <ProForm
            name='source'
            preserve
            layout={'vertical'}
            className={classNames(`flex overflow-hidden gap-x-[24px] ${!collapsed ? 'flex-wrap' : 'flex-wrap h-[69px]'}`, collapsed ? styles.tdsFormItemCollapsed : '')}
            submitter={false}
            onFinish={async () => {
              onSubmit();
              return true;
            }}
            onFieldsChange={onFieldsChange}
            formRef={sourceFormRef}
            {...formProps}
          >
            {renderFormItems('source')}
            {showFilterPanel && (!isCompare && !collapsed) ? <div className={'mt-[22px] mb-[18px]'}>
              <TdsFilterPanel
              entries={editableItems}
              defaultSelectedEntries={filteredItems}
              onSelectedChange={onSelectedChange}
              renderByGroup={props.renderByGroup}
            />
            </div> : null}
          </ProForm>
        </div>
        {isCompare && <Divider className='my-[16px]' />}

        <div className={classNames(styles.tdsFormContainer, !isCompare ? 'hidden' : '')}>
          <div className={styles.compareLabel}>
            <span className={styles.compareText}>条件</span>
            <img src={CompareFormB} className={styles.compareImg}/>
          </div>
          <ProForm
            name='compare'
            preserve
            layout={'vertical'}
            className={classNames(`flex overflow-hidden gap-x-[24px] ${!collapsed ? 'flex-wrap' : 'flex-wrap h-[69px]'}`, collapsed ? styles.tdsFormItemCollapsed : '')}
            submitter={false}
            onFinish={async () => {
              onSubmit();
              return true;
            }}
            onFieldsChange={onFieldsChange}
            formRef={targetFormRef}
            {...formProps}
          >
            {renderFormItems('target')}
          </ProForm>
        </div>
      </div>
      {isCompare && <Divider className='my-[16px]' />}
      {showToolbar && <div className={'flex'}>
        <div className={'flex-1'}>
          {showSearchHistory && <TdsSearchHistory
            searchValues={searchValues}
            setSearchValues={setSearchValues}
            form={sourceFormRef?.current}
            onFinish={onSubmit}
          />}
        </div>
        <div className={'gap-x-[16px] flex items-center'}>
          {((showFilterPanel && (isCompare || collapsed))) ? <TdsFilterPanel
            entries={editableItems}
            defaultSelectedEntries={filteredItems}
            onSelectedChange={onSelectedChange}
            renderByGroup={props.renderByGroup}
          /> : null}
          <BubbleWithProUpgrade desc={'专业版支持更丰富的分析能力'}>
            {enableCompare && (
              <div>
                <Button disabled={isFreeProduct()} style={{
                  border: '1px solid rgb(10, 108, 255)',
                  color: 'rgb(10, 108, 255)',
                }} onClick={toggleCompare}>{compareText}</Button>
              </div>
            )}
          </BubbleWithProUpgrade>
          {(showSubmit && !isCompare) ? <Button type={'primary'} onClick={() => onSubmit()}>查询</Button> : null}
          {(showSubmit && isCompare) ? <Button type={'primary'} onClick={() => onCompareSubmit()}>查询</Button> : null}
          {showToggle && <a onClick={onToggle} className={'flex gap-[4px] items-center'}>
            {!collapsed ? '收起' : '展开'}
            {!collapsed ? <UpOutlined/> : <DownOutlined/>}
          </a>}
        </div>
      </div>}
    </div>
  );
});

interface IFormFieldProps {
  item: TdsFormItem;
  itemWidth: number;
  splitRef: React.MutableRefObject<any>
  onBatchEditConfirm: () => void;
  showModal: (item: TdsFormItem, formType: string) => void;
  formType: string;
}

const FormField: React.FC<IFormFieldProps> = (props) => {
  const { item, onBatchEditConfirm, splitRef, itemWidth, showModal, formType } = props;
  const {
    width,
    hidden,
    type,
    fieldProps,
    style,
    label,
    labelString,
    labelTools,
    ...itemProps
  } = item;
  const domRef = useRef(null);
  const isHovering = useHover(domRef);

  return <div className={styles.tdsLabel} ref={domRef}><ProFormField
  name={item.name}
  label={<TdsFormLabel isHovering={isHovering}
                       label={labelString || label}
                       tools={[
                         item.batchEditMode
                           ? <SplitModal ref={splitRef}
                               callBack={onBatchEditConfirm}
                               onTrigger={() => showModal(item, formType)} trigger={hintChildren(
                                 (
                                   <span className={'underline cursor-pointer flex align-center'}><StringTooltipIcon /></span>
                                 ), (
                             <div>
                               <div>{item.batchEditTips}</div>
                               <div onClick={() => showModal(item, formType)} className={'underline cursor-pointer'}>点击icon进入批量输入编辑页</div>
                             </div>
                                 ),
                               )} /> : undefined,
                         ...(labelTools || []),
                       ]}/>}
  {...itemProps}
  style={{
    width: itemWidth,
    ...style,
  }}
>
  {props?.children}
</ProFormField></div>;
};
